//  ----------------------------------------------------------------------------
//  Dependencies
//  ----------------------------------------------------------------------------
//  -- Components ---------------------
import {
    UserOutlined, 
    IdcardOutlined,
    BankOutlined, 
    CheckCircleOutlined, 
    CodeOutlined, 
    StarOutlined, 
    CalendarOutlined, 
    BookOutlined,
    GlobalOutlined
} from '@ant-design/icons'; 
import { Card, CardContent, Grid, IconButton, Typography } from '@mui/material';
import { Button, Col, Row, message } from 'antd';
import { lighten } from 'polished';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import styled from 'styled-components';
import COLOR from "utils/constants/color";
import { QuizReview } from './SubmissionDisplay';
import moment from 'moment-timezone';
import { Tooltip } from "antd";
import { useCurrentOrg } from 'store/organizations/selectors';
import { PrintOutlined } from '@mui/icons-material';
import { useReactToPrint } from "react-to-print";
import Loading from 'components/Loading';
import { usePlacementOrgConfig } from 'store/placement_exam/selectors';
import { placement_exam_logo } from 'utils/assets/images';
import StudentResultGraph from './StudentResultGraph';
import { disableBodyScroll } from 'utils/methods/math';
import SentryWrap from 'components/Sentry';
import {
    getNativeNonNativeConfigRows,
    getNativeNonNativeLabelFromPoints,
} from "components/PlacementExam/NativeNonNativeConfiguration";
import { useCurrentUser } from 'store/users/selectors';
import { handlePlacementExamAiReGrading } from 'store/placement_exam/thunks';
//  -- Constants ----------------------
const AI_Review_message = `The AI is currently reviewing the student’s answer. Grading and feedback will appear within 10 minutes.`
const SUBMISSION_NOT_COMPLETED = `This submission has not been completed by the student, you can review the partial responses provided.`
const Notification = styled.div`
    background-color: ${p => p.bgColor ? p.bgColor : lighten(0.45, '#3D5F8F')};
    border: 1px dashed ${p => p.bgColor ? p.color : '#3D5F8F'};
    border-radius: 3px;
    color: ${p => p.color ? p.color : '#3D5F8F'};
    font-size: 12px;
    font-weight: 600;
    margin-bottom: 24px;
    padding: 8px;
`;

const Wrapper = styled.div`
    margin: 0px auto;
    width: 100%;
    position: relative;
    top: 0px;
    min-height: 70vh;
    max-height: 92vh;
    overflow-y: auto;
    overflow-x: hidden;

    /* For screens larger than 1300px */
    @media (min-width: 1300px) {
        width: 85%;
    }

    /* For screens between 1000px and 1299px */
    @media (min-width: 1000px) and (max-width: 1299px) {
        width: 85%;
    }

    /* For screens between 700px and 999px */
    @media (min-width: 700px) and (max-width: 999px) {
        width: 90%;
    }

    /* For screens smaller than 700px */
    @media (max-width: 699px) {
        width: 100%;
    }
  @media print {
    width: 100% !important;
    position: static !important;
    top: 0 !important;
    min-height: auto !important;
    max-height: none !important;
    height: auto !important;
    overflow: visible !important;
    margin: 0 !important;
    padding: 0 !important;
    box-shadow: none !important;
    background: none !important;
    // page-break-inside: avoid !important;
    // break-inside: avoid !important;
  }
`;

// Styled Components
const InfoGrid = styled(Grid)`
    margin-bottom: 16px;
    background-color: ${COLOR.indigo100}; /* Dark purple background */
    color: #fff;
    min-height: 110px;
    padding: 16px;
    border-radius: 8px;
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.3);
    transition: transform 0.2s, box-shadow 0.2s;
`;

const InfoRow = styled(Row)`
    width: 100%;
`;

const InfoCol = styled(Col)`
    display: flex;
    align-items: center;
    gap: 8px;
`;

const InfoText = styled.p`
    font-size: 12px;
    font-weight: 500;
    color: #fff;
    margin: 0;
    display: flex;
    @media (max-width: 1168px) {
      font-size: 12px;
    }
`;

const IconWrapper = styled.span`
    font-size: 20px;
    color: #fff; /* Coral color for icons */
`;
const UserValue = styled.p`
    font-size: 12px;
    font-weight: 200;
    color: #fff;
    white-space: nowrap; 
    margin: 0;
    overflow: hidden;
    text-overflow: ellipsis;
    margin-left:3px;
    min-width:200px;
    font-family: sans-serif;
`;
const GradeActionWrap = styled.div`
    display: flex;
    align-items: center;
    gap: 8px;
    margin-left: 3px;
    flex-wrap: wrap;
`;
const RegradeButton = styled(Button)`
    border-radius: 6px !important;
    height: 22px !important;
    padding: 0 10px !important;
    font-size: 11px !important;
    font-weight: 600 !important;
    background: #0284c7 !important;
    border-color: #0284c7 !important;
    box-shadow: none !important;
    margin-left: 15px;
`;
const PrintButton = styled(IconButton)`
  @media print {
    visibility: hidden !important;
    display: none !important;
  }
`;
const QuizReviewArea = styled.div`
    @media print {
        // page-break-inside: avoid !important;
    }
`;
const LogoImg = styled.img`
 max-width:100%;
`;
const DefaultLogoImg = styled.img`
  height: 150px;
  width: auto;
  border-radius: 12px;
  border: 4px solid #03999e;
`;
const StyledCard = styled(Card)`
  background-color: #f5f5ff;
  border-radius: 16px;
  padding: 8px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
`;
const Section = styled.div`
//   page-break-inside: avoid;
//   break-inside: avoid;
  margin-bottom: 0px;

  @media print {
    // page-break-inside: avoid;
    // break-inside: avoid;
  }
`;

const UserInfoArea = ({ submission , assignment ,printLoader , onPrint, onRegrade, regradeLoader }) => {
    const organization = useCurrentOrg()
    const orgConfig = usePlacementOrgConfig()
    const assignmentCodeForConfig = useMemo(
        () => assignment?.assigned?.assignment_code || submission?.assignment_code || "",
        [assignment, submission]
    );
    const maxExamPoints = useMemo(() => {
        if (!assignment?.questions?.length) return 0;
        return assignment.questions.reduce((s, q) => s + Number(q.max_point || 0), 0);
    }, [assignment]);
    const nativeNonNativeRows = useMemo(
        () => getNativeNonNativeConfigRows(orgConfig, assignmentCodeForConfig),
        [orgConfig, assignmentCodeForConfig]
    );
    const nativeNonNativeLabel = useMemo(() => {
        if (!submission || (submission.grade !== 0 && !submission.grade)) return "-";
        return getNativeNonNativeLabelFromPoints(submission.grade, maxExamPoints, nativeNonNativeRows);
    }, [submission, maxExamPoints, nativeNonNativeRows]);
    if (!submission || !submission.id || !assignment) return null;
    const fullName = `${submission.firstName} ${submission.lastName}`;
    const {  displayName  } = assignment;
    const examName = displayName || submission.assignment_code;
    const getGradeLabel = (submission) => {
        if (submission && submission.grade > 0 || submission.grade == 0) return Math.ceil(submission.grade);
        if(submission?.graded) return '0';
        return '-';
    }
    return (
        <InfoGrid container direction="column" alignItems="center">
            <InfoRow>
                <InfoCol  xl={20} lg={20} sm={14} xs={14} style={{ display: "flex", flexDirection: "column" }}>
                    <InfoRow>
                        <InfoCol xl={12} lg={12} sm={24} xs={24}>
                            <IconWrapper>
                                <UserOutlined />
                            </IconWrapper>
                            <InfoText variant="body1">Name:</InfoText>
                            <UserValue> <Tooltip title={fullName} placement="top">{fullName}</Tooltip></UserValue>
                        </InfoCol>
                        <InfoCol xl={12} lg={12} sm={24} xs={24}>
                            <IconWrapper>
                                <CodeOutlined />
                            </IconWrapper>
                            <InfoText variant="body1">Exam:</InfoText>
                            <UserValue> <Tooltip title={examName} placement="top">{examName} </Tooltip> </UserValue>
                        </InfoCol>
                    </InfoRow>


                    <InfoRow>
                        <InfoCol xl={12} lg={12} sm={24} xs={24}>
                            <IconWrapper>
                                <IdcardOutlined />
                            </IconWrapper>
                            <InfoText variant="body1">Student ID:</InfoText>
                            <UserValue>{submission.student_id}</UserValue>
                        </InfoCol>
                        <InfoCol xl={12} lg={12} sm={24} xs={24}>
                            <IconWrapper>
                                <CheckCircleOutlined />
                            </IconWrapper>
                            <InfoText variant="body1">Completed:</InfoText>
                            <UserValue>{submission.completed ? "Yes" : "No"}</UserValue>
                        </InfoCol>
                    </InfoRow>
                    <InfoRow>
                        <InfoCol xl={12} lg={12} sm={24} xs={24}>
                            <IconWrapper>
                                <BankOutlined />
                            </IconWrapper>
                            <InfoText variant="body1">District:</InfoText>
                            <UserValue> <Tooltip title={organization?.displayName || '-'} placement="top"> {organization?.displayName || '-'}</Tooltip></UserValue>
                        </InfoCol>
                        <InfoCol xl={12} lg={12} sm={24} xs={24}>
                            <IconWrapper>
                                <BankOutlined />
                            </IconWrapper>
                            <InfoText variant="body1">School Name: </InfoText>
                            <UserValue><Tooltip title={submission.school_name || '-'} placement="top">{submission.school_name}</Tooltip></UserValue>
                        </InfoCol>
                    </InfoRow>

                    <InfoRow>
                        <InfoCol xl={12} lg={12} sm={24} xs={24}>
                            <IconWrapper>
                                <BookOutlined />
                            </IconWrapper>
                            <InfoText variant="body1">Grade level:</InfoText>
                            <UserValue>{submission.grade_level || '-'}</UserValue>
                        </InfoCol>
                        <InfoCol xl={12} lg={12} sm={24} xs={24}>
                            <IconWrapper>
                                <CalendarOutlined />
                            </IconWrapper>
                            <InfoText variant="body1">Submitted on:</InfoText>
                            <UserValue>{submission.created_on ? moment(submission.created_on).format("MM-DD-YYYY hh:mm A") : "-"}</UserValue>
                        </InfoCol>
                    </InfoRow>
                    <InfoRow>
                        <InfoCol xl={12} lg={12} sm={24} xs={24}>
                            <IconWrapper>
                                <StarOutlined />
                            </IconWrapper>
                            <InfoText variant="body1">Grade:</InfoText>
                            <GradeActionWrap>
                                <UserValue>{getGradeLabel(submission)}
                                {submission?.graded && onRegrade ? (
                                    <RegradeButton
                                        type="primary"
                                        onClick={onRegrade}
                                        loading={regradeLoader}
                                    >
                                 Re-grade
                                    </RegradeButton>
                                ) : null}
                                </UserValue>
                            </GradeActionWrap>
                        </InfoCol>
                        <InfoCol xl={12} lg={12} sm={24} xs={24}>
                            <IconWrapper>
                                <GlobalOutlined />
                            </IconWrapper>
                            <InfoText variant="body1">Native/NonNative:</InfoText>
                            <UserValue>{nativeNonNativeLabel}</UserValue>
                        </InfoCol>
                    </InfoRow>
                </InfoCol>
                <InfoCol   xl={4} lg={4} sm={10} xs={10} style={{ display: "flex", flexDirection: "column" }}>
                    <div className="studentFormLogo mngeCoronaLogo 111">
                        {orgConfig&& orgConfig.logo ?<LogoImg src={orgConfig.logo} alt="Placement District Logo" />:<DefaultLogoImg src={placement_exam_logo} alt="Placement District Logo" />}
                    </div>
                    <PrintButton onClick={onPrint} disabled={printLoader}>
                        {printLoader ? <Loading size={20} /> : <PrintOutlined style={{ color: "#fff" }} />}
                    </PrintButton>
                </InfoCol>
            </InfoRow>
        </InfoGrid>
    );
};

const StudentTextResponses = ({ responses }) => {
    if (!responses || Object.keys(responses).length === 0) return null;

    return (
        <StyledCard style={{ marginTop: 32, width: "100%" }}>
            <Grid item spacing={3} className='printable printableCstm'>
                <CardContent style={{
                    marginBottom: 24,
                    padding: 16,
                    background: "#f9f9f9",
                    borderRadius: 8,
                    border: "1px solid #eee",
                    width:"100%"
                }}>
                    <Typography variant="h5" style={{ marginBottom: 16 }}>
                        Student Responses
                    </Typography>
                    <div>
                        {Object.entries(responses).map(([key, { question, answer }], idx) => (
                            <div
                                key={key}
                                style={{
                                    marginBottom:8,
                                    marginTop:8,
                                    width:"100%"
                                }}
                            >
                                <Typography variant="subtitle1" style={{ fontWeight: 600 }}>
                                    {`Q${idx + 1}: ${question}`}
                                </Typography>
                                <Typography variant="subtitle2" style={{ marginTop: 8 }}>
                                    {answer || <i style={{ color: "#999" }}>No response</i>}
                                </Typography>
                            </div>
                        ))}
                    </div>
                </CardContent>
            </Grid>
        </StyledCard>
    );
};

const PlacementExamViewSubmission = ({ submission, assignment, onOk = () => { } , overAllData }) => {
    const [answers, setAnswers] = useState([]);
    const [sections, setSections] = useState([]);
    const [printLoader, setPrintloader] = useState(false)
    const [regradeLoader, setRegradeLoader] = useState(false);
    const [currentSubmission, setCurrentSubmission] = useState(submission);
    const imageRef = useRef();
    const isPrintingRef = useRef(false);
    const currentUser = useCurrentUser();
    const isSuperAdmin = useMemo(() => currentUser?.role === "owe_admin", [currentUser?.role]);
    const max_point = useMemo(() => 
        assignment && Array.isArray(assignment.questions) 
          ? assignment.questions.reduce((prev, currentVal) => prev + Number(currentVal.max_point||0), 0) 
          : 100,
        [assignment]
    );
    const user_grade = useMemo(
        () => (currentSubmission && (currentSubmission.grade || currentSubmission.grade === 0) ? Math.ceil(currentSubmission.grade) : 0),
        [currentSubmission]
    );
    useEffect(() => {
        setCurrentSubmission(submission);
    }, [submission]);
    useEffect(() => {
        if (currentSubmission && currentSubmission.answers) {
            setAnswers(currentSubmission.answers);
        }
        if (assignment && assignment.questions) {
            setSections(assignment.questions);
        }
    }, [currentSubmission, assignment]);
    useEffect(() => {
        document.documentElement.style.overflow = "hidden";
        disableBodyScroll('PlacementExamViewSubmission');

        return () => {
            document.documentElement.style.overflow = "auto";
            document.body.style.overflow = "auto";
        };
    }, []);
    const printCall= useReactToPrint({
        contentRef: imageRef,
        pageStyle: `
          @page {
            size: Letter;
            margin-top: 2mm !important;
          }
          body {
            -webkit-print-color-adjust: exact;
            print-color-adjust: exact;
          }
          .placement-exam-print {
            margin: 0 !important;
            padding: 0 !important;
          }
        `,
        onBeforePrint: () => {
          setPrintloader(true);
          if (imageRef.current) {
            imageRef.current.style.height = "auto";
            imageRef.current.style.overflow = "visible";
          }
          return Promise.resolve();
        },
        onAfterPrint: async () => {
          setPrintloader(false);
          isPrintingRef.current = false;
          if (imageRef.current) {
            imageRef.current.style.height = "";
            imageRef.current.style.overflow = "";
          }
        },
        onPrintError: () => {
          setPrintloader(false);
          isPrintingRef.current = false;
          if (imageRef.current) {
            imageRef.current.style.height = "";
            imageRef.current.style.overflow = "";
          }
        }
      });
    const waitForPrintReady = useCallback(async () => {
        if (!imageRef.current) return;

        const node = imageRef.current;
        const images = Array.from(node.querySelectorAll('img'));

        // Wait until all not-yet-loaded images in the print area are ready.
        await Promise.all(
          images
            .filter((img) => !img.complete)
            .map(
              (img) =>
                new Promise((resolve) => {
                  const done = () => resolve(true);
                  img.addEventListener('load', done, { once: true });
                  img.addEventListener('error', done, { once: true });
                  setTimeout(done, 1500);
                })
            )
        );

        if (document.fonts?.ready) {
          await document.fonts.ready;
        }

        // Ensure layout settles before opening browser print dialog.
        await new Promise((resolve) => requestAnimationFrame(() => requestAnimationFrame(resolve)));
      }, []);

    const handlePrint = useCallback(async () => {
        if (!imageRef.current || !printCall || isPrintingRef.current) return;
        isPrintingRef.current = true;
        setPrintloader(true);
        await waitForPrintReady();
        printCall();
    }, [printCall, waitForPrintReady]);
    const handleRegrade = useCallback(async () => {
        if (!isSuperAdmin || !currentSubmission?.graded || !currentSubmission?.id || regradeLoader) return;
        try {
            setRegradeLoader(true);
            const response = await handlePlacementExamAiReGrading(currentSubmission.id);
            if (response && !response.error) {
                if (response.update_data) {
                    setCurrentSubmission(response.update_data);
                }
                message.success("Re-grade triggered successfully");
            } else {
                message.error(response?.message || "Could not trigger re-grade");
            }
        } catch (error) {
            message.error("Could not trigger re-grade");
        } finally {
            setRegradeLoader(false);
        }
    }, [isSuperAdmin, currentSubmission, regradeLoader]);
    return (
        <SentryWrap>
            <Wrapper ref={imageRef} className='printable placement-exam-print'>
                {currentSubmission && currentSubmission.completed && typeof currentSubmission.graded && !currentSubmission.graded ? 
                <Section className='allowNotifyLabel'><Notification bgColor={COLOR.indigo100} color={COLOR.white}>
                    {AI_Review_message}
                </Notification></Section> : null}
                {currentSubmission && !currentSubmission.completed ? <Section className='allowNotifyLabel'><Notification bgColor={COLOR.WheelEditColor1} color={COLOR.white}>
                    {SUBMISSION_NOT_COMPLETED}
                </Notification></Section> : null}
                <Section>
                    <UserInfoArea
                        submission={currentSubmission}
                        assignment={assignment}
                        onPrint={handlePrint}
                        printLoader={printLoader}
                        onRegrade={isSuperAdmin ? handleRegrade : undefined}
                        regradeLoader={regradeLoader}
                    />
                </Section>
                <Section><StudentResultGraph student_marks={user_grade} max_marks={max_point||100} overAllData={overAllData ||{}} active_exam={assignment}/></Section>
                <Section><StudentTextResponses responses={currentSubmission?.session_respone} /></Section>
                <Section><QuizReviewArea>
                    <QuizReview
                        sections={sections}
                        answers={answers}
                        submission={currentSubmission}
                    />

                </QuizReviewArea></Section>
            </Wrapper>
        </SentryWrap>
    );
};



export default PlacementExamViewSubmission;
